/**
 * Offline-first patterns and background sync queue management
 *
 * Provides comprehensive sync queue management, conflict resolution preparation,
 * and background synchronization with retry logic and error handling.
 */

import { db } from '../database/schema.js'
import type { SyncEvent } from '../database/schema.js'
import { BaseStorageService, type StorageResult, StorageUtils } from './base-storage.js'

/**
 * Sync queue entry with priority and retry logic
 */
export interface SyncQueueEntry extends SyncEvent {
  priority: 'low' | 'normal' | 'high' | 'critical'
  retryCount: number
  maxRetries: number
  nextRetryAt?: string | undefined
  error?: string | undefined
  dependencies?: string[] | undefined // IDs of other sync events this depends on
}

/**
 * Sync operation result
 */
export interface SyncOperationResult {
  success: boolean
  syncEventId: string
  entityId: string
  operation: string
  error?: string | undefined
  conflictData?: unknown | undefined
  serverResponse?: unknown | undefined
}

/**
 * Sync batch result
 */
export interface SyncBatchResult {
  totalProcessed: number
  successful: number
  failed: number
  conflicts: number
  results: SyncOperationResult[]
}

/**
 * Sync statistics
 */
export interface SyncStatistics {
  totalPending: number
  totalFailed: number
  totalSynced: number
  oldestPending?: string | undefined
  lastSyncAt?: string | undefined
  averageRetryCount: number
  conflictCount: number
  queueSizeBytes: number
}

/**
 * Conflict resolution strategy
 */
export type ConflictResolutionStrategy = 'client-wins' | 'server-wins' | 'manual' | 'merge'

/**
 * Sync configuration
 */
export interface SyncConfiguration {
  batchSize: number
  retryDelayMs: number
  maxRetries: number
  onlineCheckIntervalMs: number
  conflictResolution: ConflictResolutionStrategy
  priorityWeights: {
    low: number
    normal: number
    high: number
    critical: number
  }
}

/**
 * Default sync configuration
 */
const DEFAULT_SYNC_CONFIG: SyncConfiguration = {
  batchSize: 10,
  retryDelayMs: 5000, // 5 seconds
  maxRetries: 3,
  onlineCheckIntervalMs: 10000, // 10 seconds
  conflictResolution: 'manual',
  priorityWeights: {
    low: 1,
    normal: 2,
    high: 3,
    critical: 4,
  },
}

/**
 * Sync queue storage service
 */
export class SyncQueueService extends BaseStorageService<SyncEvent> {
  private config: SyncConfiguration
  private isProcessing = false
  private syncInterval?: number | undefined
  private syncHandler?: ((event: SyncQueueEntry) => Promise<SyncOperationResult>) | undefined
  private callbacks = {
    onSync: [] as ((result: SyncOperationResult) => void)[],
    onConflict: [] as ((conflict: unknown) => void)[],
    onComplete: [] as ((batch: SyncBatchResult) => void)[],
  }

  constructor(config: Partial<SyncConfiguration> = {}) {
    super(db.syncEvents, 'syncEvent')
    this.config = { ...DEFAULT_SYNC_CONFIG, ...config }

    // Pause auto-sync when app is hidden to avoid unnecessary wake-ups
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && this.syncInterval) {
          this.stopAutoSync()
        } else if (
          document.visibilityState === 'visible' &&
          this.syncHandler &&
          !this.syncInterval
        ) {
          this.startAutoSync(this.syncHandler)
        }
      })
    }
  }

  /**
   * Add event to sync queue
   */
  async enqueue(
    entityType: string,
    entityId: string,
    operation: 'create' | 'update' | 'delete',
    data?: Record<string, unknown>,
    priority: SyncQueueEntry['priority'] = 'normal',
    dependencies: string[] = []
  ): Promise<StorageResult<SyncEvent>> {
    return this.executeOperation(async () => {
      const id = this.generateId()
      const now = new Date().toISOString()

      const syncEvent: SyncEvent = {
        id,
        entityType,
        entityId,
        operation,
        data: data ?? {},
        timestamp: now,
        conflictResolved: false,
      }

      // Store additional queue metadata in data field
      const queueEntry: SyncQueueEntry = {
        ...syncEvent,
        priority,
        retryCount: 0,
        maxRetries: this.config.maxRetries,
        dependencies,
      }

      await this.table.add(queueEntry)
      return syncEvent
    }, 'enqueue sync event')
  }

  /**
   * Get pending sync events ordered by priority
   */
  async getPendingEvents(
    limit: number = this.config.batchSize
  ): Promise<StorageResult<SyncQueueEntry[]>> {
    return this.executeOperation(async () => {
      const allEvents = (await this.table
        .filter(event => !(event as SyncQueueEntry).syncedAt)
        .toArray()) as SyncQueueEntry[]

      const now = new Date()

      // Filter events ready for processing (not waiting for retry delay)
      const readyEvents = allEvents.filter(event => {
        if (event.nextRetryAt) {
          return new Date(event.nextRetryAt) <= now
        }
        return true
      })

      // Sort by priority and timestamp
      const sortedEvents = readyEvents.sort((a, b) => {
        const priorityDiff =
          this.config.priorityWeights[b.priority] - this.config.priorityWeights[a.priority]
        if (priorityDiff !== 0) {
          return priorityDiff
        }
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      })

      return sortedEvents.slice(0, limit)
    }, 'get pending events')
  }

  /**
   * Mark sync event as completed
   */
  async markSynced(syncEventId: string, serverResponse?: unknown): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const event = (await this.table.get(syncEventId)) as SyncQueueEntry | undefined
      if (!event) {
        throw new Error('Sync event not found')
      }

      const updatedEvent: Partial<SyncQueueEntry> = {
        syncedAt: new Date().toISOString(),
        data: {
          ...event.data,
          serverResponse,
        },
      }

      await this.table.update(syncEventId, updatedEvent)
      return undefined
    }, 'mark synced')
  }

  /**
   * Mark sync event as failed and schedule retry
   */
  async markFailed(
    syncEventId: string,
    error: string,
    scheduleRetry = true
  ): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const event = (await this.table.get(syncEventId)) as SyncQueueEntry | undefined
      if (!event) {
        throw new Error('Sync event not found')
      }

      const retryCount = (event.retryCount || 0) + 1
      const updates: Partial<SyncQueueEntry> = {
        retryCount,
        error,
      }

      if (scheduleRetry && retryCount <= this.config.maxRetries) {
        // Calculate exponential backoff
        const delay = this.config.retryDelayMs * Math.pow(2, retryCount - 1)
        updates.nextRetryAt = new Date(Date.now() + delay).toISOString()
      }

      await this.table.update(syncEventId, updates)
      return undefined
    }, 'mark failed')
  }

  /**
   * Mark sync event as having conflict
   */
  async markConflict(
    syncEventId: string,
    conflictData: unknown,
    strategy: ConflictResolutionStrategy = this.config.conflictResolution
  ): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const updates: Partial<SyncQueueEntry> = {
        conflictResolved: false,
        data: {
          conflictData,
          resolutionStrategy: strategy,
        },
      }

      await this.table.update(syncEventId, updates)

      // Notify conflict handlers
      this.callbacks.onConflict.forEach(callback => callback(conflictData))

      return undefined
    }, 'mark conflict')
  }

  /**
   * Resolve conflict manually
   */
  async resolveConflict(
    syncEventId: string,
    resolution: unknown,
    strategy: ConflictResolutionStrategy
  ): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const event = (await this.table.get(syncEventId)) as SyncQueueEntry | undefined
      if (!event) {
        throw new Error('Sync event not found')
      }

      const updates: Partial<SyncQueueEntry> = {
        conflictResolved: true,
        data: {
          ...event.data,
          resolution,
          resolutionStrategy: strategy,
          resolvedAt: new Date().toISOString(),
        },
      }

      await this.table.update(syncEventId, updates)
      return undefined
    }, 'resolve conflict')
  }

  /**
   * Process sync queue batch
   */
  async processBatch(
    syncHandler: (event: SyncQueueEntry) => Promise<SyncOperationResult>
  ): Promise<StorageResult<SyncBatchResult>> {
    if (this.isProcessing) {
      return {
        success: false,
        error: this.createError('ALREADY_PROCESSING', 'Sync batch already in progress'),
      }
    }

    this.isProcessing = true

    try {
      return await this.executeOperation(async () => {
        const pendingResult = await this.getPendingEvents()
        if (!pendingResult.success || !pendingResult.data) {
          return {
            totalProcessed: 0,
            successful: 0,
            failed: 0,
            conflicts: 0,
            results: [],
          }
        }

        const events = pendingResult.data
        const results: SyncOperationResult[] = []
        let successful = 0
        let failed = 0
        let conflicts = 0

        for (const event of events) {
          try {
            // Check dependencies
            if (event.dependencies && event.dependencies.length > 0) {
              const dependenciesMet = await this.checkDependencies(event.dependencies)
              if (!dependenciesMet) {
                // Skip this event for now
                continue
              }
            }

            const result = await syncHandler(event)
            results.push(result)

            if (result.success) {
              await this.markSynced(event.id, result.serverResponse)
              successful++
            } else if (result.conflictData) {
              await this.markConflict(event.id, result.conflictData)
              conflicts++
            } else {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use default
              await this.markFailed(event.id, result.error || 'Unknown error')
              failed++
            }

            // Notify sync handlers
            this.callbacks.onSync.forEach(callback => callback(result))
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            await this.markFailed(event.id, errorMessage)
            failed++

            results.push({
              success: false,
              syncEventId: event.id,
              entityId: event.entityId,
              operation: event.operation,
              error: errorMessage,
            })
          }
        }

        const batchResult: SyncBatchResult = {
          totalProcessed: events.length,
          successful,
          failed,
          conflicts,
          results,
        }

        // Notify completion handlers
        this.callbacks.onComplete.forEach(callback => callback(batchResult))

        return batchResult
      }, 'process sync batch')
    } finally {
      this.isProcessing = false
    }
  }

  /**
   * Start automatic sync processing
   */
  startAutoSync(syncHandler: (event: SyncQueueEntry) => Promise<SyncOperationResult>): void {
    this.syncHandler = syncHandler

    if (this.syncInterval) {
      this.stopAutoSync()
    }

    this.syncInterval = window.setInterval(() => {
      if (StorageUtils.isOnline() && !this.isProcessing) {
        void this.processBatch(syncHandler)
      }
    }, this.config.onlineCheckIntervalMs)
  }

  /**
   * Stop automatic sync processing
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
      this.syncInterval = undefined
    }
  }

  /**
   * Get sync statistics
   */
  async getStatistics(): Promise<StorageResult<SyncStatistics>> {
    return this.executeOperation(async () => {
      const allEvents = (await this.table.toArray()) as SyncQueueEntry[]

      const pending = allEvents.filter(event => !event.syncedAt)
      const synced = allEvents.filter(event => event.syncedAt)
      const failed = allEvents.filter(event => event.error && !event.syncedAt)
      const conflicts = allEvents.filter(
        event => !event.conflictResolved && event.data.conflictData
      )

      const oldestPending =
        pending.length > 0
          ? pending.reduce((oldest, event) =>
              new Date(event.timestamp) < new Date(oldest.timestamp) ? event : oldest
            ).timestamp
          : undefined

      const lastSynced =
        synced.length > 0
          ? synced.reduce((latest, event) =>
              new Date(event.syncedAt!) > new Date(latest.syncedAt!) ? event : latest
            ).syncedAt
          : undefined

      const totalRetries = allEvents.reduce((sum, event) => sum + (event.retryCount || 0), 0)
      const averageRetryCount = allEvents.length > 0 ? totalRetries / allEvents.length : 0

      // Estimate queue size
      const queueSizeBytes = allEvents.reduce((size, event) => {
        const dataSize = JSON.stringify(event).length * 2 // Rough UTF-16 estimation
        return size + dataSize
      }, 0)

      return {
        totalPending: pending.length,
        totalFailed: failed.length,
        totalSynced: synced.length,
        oldestPending,
        lastSyncAt: lastSynced,
        averageRetryCount,
        conflictCount: conflicts.length,
        queueSizeBytes,
      }
    }, 'get sync statistics')
  }

  /**
   * Clear completed sync events older than specified days
   */
  async cleanupOldEvents(olderThanDays: number = 7): Promise<StorageResult<number>> {
    return this.executeOperation(async () => {
      const cutoffDate = new Date(Date.now() - olderThanDays * 24 * 60 * 60 * 1000)

      const oldEvents = await this.table.where('syncedAt').below(cutoffDate.toISOString()).toArray()

      await this.table.bulkDelete(oldEvents.map(event => event.id))

      return oldEvents.length
    }, 'cleanup old events')
  }

  /**
   * Get failed events for manual review
   */
  async getFailedEvents(): Promise<StorageResult<SyncQueueEntry[]>> {
    return this.executeOperation(async () => {
      const events = (await this.table.toArray()) as SyncQueueEntry[]
      return events.filter(
        event => event.error && !event.syncedAt && (event.retryCount || 0) >= this.config.maxRetries
      )
    }, 'get failed events')
  }

  /**
   * Get conflicts requiring manual resolution
   */
  async getUnresolvedConflicts(): Promise<StorageResult<SyncQueueEntry[]>> {
    return this.executeOperation(async () => {
      const events = (await this.table.toArray()) as SyncQueueEntry[]
      return events.filter(event => event.data.conflictData && !event.conflictResolved)
    }, 'get unresolved conflicts')
  }

  /**
   * Retry failed event manually
   */
  async retryEvent(syncEventId: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const updates: Partial<SyncQueueEntry> = {
        retryCount: 0,
        error: undefined,
        nextRetryAt: undefined,
      }

      await this.table.update(syncEventId, updates)
      return undefined
    }, 'retry event')
  }

  /**
   * Add event handlers
   */
  on(event: 'sync', handler: (result: SyncOperationResult) => void): void
  on(event: 'conflict', handler: (conflict: unknown) => void): void
  on(event: 'complete', handler: (batch: SyncBatchResult) => void): void
  on(
    event: 'sync' | 'conflict' | 'complete',
    handler:
      | ((result: SyncOperationResult) => void)
      | ((conflict: unknown) => void)
      | ((batch: SyncBatchResult) => void)
  ): void {
    switch (event) {
      case 'sync':
        this.callbacks.onSync.push(handler as (result: SyncOperationResult) => void)
        break
      case 'conflict':
        this.callbacks.onConflict.push(handler as (conflict: unknown) => void)
        break
      case 'complete':
        this.callbacks.onComplete.push(handler as (batch: SyncBatchResult) => void)
        break
    }
  }

  /**
   * Remove event handlers
   */
  off(
    event: 'sync' | 'conflict' | 'complete',
    handler?:
      | ((result: SyncOperationResult) => void)
      | ((conflict: unknown) => void)
      | ((batch: SyncBatchResult) => void)
  ): void {
    switch (event) {
      case 'sync':
        if (handler) {
          this.callbacks.onSync = this.callbacks.onSync.filter(h => h !== handler)
        } else {
          this.callbacks.onSync = []
        }
        break
      case 'conflict':
        if (handler) {
          this.callbacks.onConflict = this.callbacks.onConflict.filter(h => h !== handler)
        } else {
          this.callbacks.onConflict = []
        }
        break
      case 'complete':
        if (handler) {
          this.callbacks.onComplete = this.callbacks.onComplete.filter(h => h !== handler)
        } else {
          this.callbacks.onComplete = []
        }
        break
    }
  }

  /**
   * Check if all dependencies are synced
   */
  private async checkDependencies(dependencyIds: string[]): Promise<boolean> {
    for (const depId of dependencyIds) {
      const event = (await this.table.get(depId)) as SyncQueueEntry
      if (!event.syncedAt) {
        return false
      }
    }
    return true
  }
}

// Create and export singleton instance
export const syncQueue = new SyncQueueService()
